import type { RoomId } from "../components/Localization";

export const AUDIO_TRACK_SCORE = 2;
export const AUDIO_COMPLETION_RATIO = 0.8;

type ListenedRange = [number, number];
type TrackListening = { duration: number; ranges: ListenedRange[] };
type AudioListeningState = Partial<Record<RoomId, Record<string, TrackListening>>>;
const STORAGE_KEY = "mariaffi-audio-listening";

function readState(): AudioListeningState {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value ? JSON.parse(value) as AudioListeningState : {};
  } catch { return {}; }
}

function writeState(state: AudioListeningState) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
  catch { /* Playback remains available without browser storage. */ }
}

function mergeRanges(ranges: ListenedRange[]) {
  const sorted = ranges.filter(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && end > start).sort((a, b) => a[0] - b[0]);
  const merged: ListenedRange[] = [];
  sorted.forEach(([start, end]) => {
    const previous = merged[merged.length - 1];
    if (!previous || start > previous[1] + 0.15) merged.push([start, end]);
    else previous[1] = Math.max(previous[1], end);
  });
  return merged;
}

export function recordListenedRange(roomId: RoomId, src: string, duration: number, start: number, end: number) {
  if (!src || !Number.isFinite(duration) || duration <= 0) return;
  const state = readState();
  const roomState = state[roomId] || {};
  const track = roomState[src] || { duration, ranges: [] };
  roomState[src] = {
    duration,
    ranges: mergeRanges([...track.ranges, [Math.max(0, Math.min(start, duration)), Math.max(0, Math.min(end, duration))]]),
  };
  state[roomId] = roomState;
  writeState(state);
}

export function getTrackListeningRatio(roomId: RoomId, src: string, duration?: number) {
  const track = readState()[roomId]?.[src];
  const totalDuration = duration && duration > 0 ? duration : track?.duration || 0;
  if (!track || totalDuration <= 0) return 0;
  const listenedSeconds = mergeRanges(track.ranges).reduce((sum, [start, end]) => sum + end - start, 0);
  return Math.min(1, listenedSeconds / totalDuration);
}

export function getRecordedRoomAudioSources(roomId: RoomId) {
  return Object.keys(readState()[roomId] || {});
}

export function getRoomAudioResult(roomId: RoomId, sources: string[]) {
  const uniqueSources = Array.from(new Set(sources.filter(Boolean)));
  const completedTracks = uniqueSources.filter((src) => getTrackListeningRatio(roomId, src) >= AUDIO_COMPLETION_RATIO).length;
  const score = completedTracks * AUDIO_TRACK_SCORE;
  const maxScore = uniqueSources.length * AUDIO_TRACK_SCORE;
  return { completedTracks, totalTracks: uniqueSources.length, score, maxScore };
}
